// frontend/src/components/help/SupportPage.jsx - WITH PROPER BACK NAVIGATION
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Mail, Phone, Clock, Send } from 'lucide-react';

const SupportPage = () => {
  const navigate = useNavigate();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    urgency: 'normal'
  });

  const handleBack = () => {
    const userType = localStorage.getItem('userType');
    if (userType === 'vendor') {
      // Go back to vendor dashboard settings tab
      navigate('/vendor/dashboard', { 
        state: { activeTab: 'settings' } 
      });
    } else {
      // Go back to previous page
      navigate(-1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real app, this would send the support request
    alert('Support request submitted! We\'ll get back to you within 24 hours.');
    setContactForm({
      name: '',
      email: '',
      subject: '',
      message: '',
      urgency: 'normal'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center">
            <button 
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
            >
              <ArrowLeft size={20} className="mr-2" />
              Back to Settings
            </button>
            <div>
              <h1 className="text-2xl font-bold">Support Center</h1>
              <p className="text-sm text-gray-600">Get help with your UniLedge account</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Contact Options */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Contact Us</h2>
              <div className="space-y-4">
                <div className="flex items-center p-4 bg-blue-50 rounded-lg">
                  <MessageSquare className="text-blue-600 mr-3" size={24} />
                  <div>
                    <h3 className="font-medium">Live Chat</h3>
                    <p className="text-sm text-gray-600">Get instant help from our support team</p>
                    <p className="text-xs text-blue-600 mt-1">Available 9 AM - 6 PM PST</p>
                  </div>
                </div>
                
                <div className="flex items-center p-4 bg-green-50 rounded-lg">
                  <Mail className="text-green-600 mr-3" size={24} />
                  <div>
                    <h3 className="font-medium">Email Support</h3>
                    <p className="text-sm text-gray-600">support@uniledge.com</p>
                    <p className="text-xs text-green-600 mt-1">Response within 24 hours</p>
                  </div>
                </div>
                
                <div className="flex items-center p-4 bg-purple-50 rounded-lg">
                  <Phone className="text-purple-600 mr-3" size={24} />
                  <div>
                    <h3 className="font-medium">Phone Support</h3>
                    <p className="text-sm text-gray-600">(916) 555-HELP</p>
                    <p className="text-xs text-purple-600 mt-1">For urgent vendor issues</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Support Hours</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Monday - Friday</span>
                  <span className="font-medium">9 AM - 6 PM PST</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Saturday</span>
                  <span className="font-medium">10 AM - 4 PM PST</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Sunday</span>
                  <span className="text-gray-500">Closed</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center">
                  <Clock className="text-yellow-600 mr-2" size={16} />
                  <p className="text-sm text-yellow-800">
                    For urgent vendor issues outside business hours, use our emergency hotline.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Send us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={contactForm.name}
                  onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Urgency
                </label>
                <select
                  value={contactForm.urgency}
                  onChange={(e) => setContactForm(prev => ({ ...prev, urgency: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">Low - General question</option>
                  <option value="normal">Normal - Need help</option>
                  <option value="high">High - Order issue</option>
                  <option value="urgent">Urgent - Payment problem</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of your issue"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Please describe your issue in detail..."
                  required
                />
              </div>
              
              <button
                type="submit"
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center"
              >
                <Send size={18} className="mr-2" />
                Send Message
              </button>
            </form>
          </div>
        </div>
        
        {/* Common Issues */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Common Issues</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium mb-3">For Vendors</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Payment processing delays</li>
                <li>• Order confirmation issues</li>
                <li>• Product listing problems</li>
                <li>• Group communication help</li>
                <li>• Account verification</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium mb-3">For Members</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Group joining problems</li>
                <li>• Token balance questions</li>
                <li>• Job application issues</li>
                <li>• Profile setup help</li>
                <li>• Skill verification</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;